import { Ref, useImperativeHandle, useMemo } from 'react';

import { useAuthentication } from '@ravago/shared/authentication';
import { UserToggles } from '@ravago/shared/page-data/models/config';
import { LeadTimeIndicator } from '@ravago/shared/page-data/models/elements/product-lead-time.model';
import { Customer } from '@ravago/shared/page-data/models/features';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Divider } from '@ravago/shared/radiance/components/divider/components/Divider/Divider';
import { InputField } from '@ravago/shared/radiance/components/input-field/components/InputField/InputField';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { Controller, useForm } from 'react-hook-form';

import { LeadTimeCheckCriteria, hasMtsLocalLeadTime } from '../../../../../../../utils/lead-time';
import { RequestForQuotationStep, RfqStep } from '../../../../models/request-for-quotation-step';
import { RfqContactInfo } from '../fields/rfq-contact-info';
import { RfqDeliveryInfo } from '../fields/rfq-delivery-info';
import { RfqProductDetails } from '../fields/rfq-product-details';

import { FormValue as AddressDetailsFormValue } from './rfq-flyout-step-address-details';
import { FormValue as ContactDetailsFormValue } from './rfq-flyout-step-contact-details';
import { FormValue as DeliveryAreaFormValue } from './rfq-flyout-step-delivery-area';
import { FormValue as QuotationDetailsFormValue } from './rfq-flyout-step-quotation-details';

export interface FormValue {
   additionalInfo: string;
}

interface Props {
   stepRef: Ref<RequestForQuotationStep>;
   customer: Customer | undefined;
   userToggles: UserToggles;
   uom: string;
   quotationDetailsFormValue: QuotationDetailsFormValue | undefined;
   addressDetailsFormValue: AddressDetailsFormValue | undefined;
   deliveryAreaFormValue: DeliveryAreaFormValue | undefined;
   contactDetailsFormValue: ContactDetailsFormValue | undefined;
   summaryFormValue: FormValue | undefined;
   setCurrentStep: (step: RfqStep) => void;
   onSummaryChange: (value: FormValue) => void;
   onSubmit: (value: FormValue) => void;
   leadTimeEnabled?: boolean;
   leadTimeIndicators?: LeadTimeIndicator[];
   selectedColor?: { colorName: string; producerColorCode: string | undefined; colorId?: number };
}

export const RfqFlyoutStepSummary = ({
   stepRef,
   customer,
   userToggles,
   uom,
   quotationDetailsFormValue,
   addressDetailsFormValue,
   deliveryAreaFormValue,
   contactDetailsFormValue,
   summaryFormValue,
   onSummaryChange,
   onSubmit,
   setCurrentStep,
   leadTimeEnabled = false,
   leadTimeIndicators = [],
   selectedColor
}: Props) => {
   const translate = useTranslations();
   const { user } = useAuthentication();

   const { control, handleSubmit } = useForm<FormValue>({
      defaultValues: summaryFormValue || { additionalInfo: '' }
   });

   const deliveryIsMtsLocal = useMemo(() => {
      if (
         !leadTimeEnabled ||
         !selectedColor?.colorId ||
         !quotationDetailsFormValue ||
         Number.isNaN(Number(quotationDetailsFormValue?.packaging?.id)) ||
         leadTimeIndicators.length === 0
      ) {
         return false;
      }

      const criteria: LeadTimeCheckCriteria = {
         colorId: selectedColor.colorId,
         producerColorCode: selectedColor.producerColorCode,
         packagingGroupId: Number(quotationDetailsFormValue.packaging.id)
      };

      if (addressDetailsFormValue?.deliveryAddress) {
         const { deliveryAddress } = addressDetailsFormValue;
         return hasMtsLocalLeadTime(leadTimeIndicators, { ...criteria, addressId: deliveryAddress.id }) || hasMtsLocalLeadTime(leadTimeIndicators, { ...criteria, countryCode: deliveryAddress.countryIsoCode });
      }

      if (deliveryAreaFormValue?.country) {
         return hasMtsLocalLeadTime(leadTimeIndicators, { ...criteria, countryCode: deliveryAreaFormValue.country.id });
      }
      return false;
   }, [leadTimeEnabled, selectedColor, quotationDetailsFormValue?.packaging?.id, addressDetailsFormValue, deliveryAreaFormValue, leadTimeIndicators]);

   useImperativeHandle(stepRef, () => ({
      submit: async () => {
         await handleSubmit((value) => onSubmit(value))();
      }
   }));

   return (
      <Box direction="column" gap="md">
         <Box direction="column" gap="sm">
            {quotationDetailsFormValue && (
               <RfqProductDetails
                  quantities={quotationDetailsFormValue.quantityBreaks.map((v) => v.value)}
                  uom={uom}
                  packaging={quotationDetailsFormValue.packaging.value}
                  otherPackagingDescription={quotationDetailsFormValue.packagingDescription}
                  setCurrentStep={setCurrentStep}
               />
            )}
         </Box>
         <Divider />
         <Box direction="column" gap="sm">
            <Typography component="span" variant="body-highlight">
               {translate('page-consumer.request-for-quotation.additional-information')}
            </Typography>
            <Controller
               control={control}
               name="additionalInfo"
               rules={{ maxLength: 250 }}
               render={({ field: { ref, onChange, ...field }, fieldState: { error } }) => (
                  <InputField
                     inputRef={ref}
                     {...field}
                     onChange={(e) => {
                        onChange(e);
                        onSummaryChange({ additionalInfo: e });
                     }}
                     label={translate('page-consumer.request-for-quotation.optional')}
                     id="rfq-additional-information"
                     supportText={translate('page-consumer.request-for-quotation.additional-information-support')}
                     error={!!error}
                     errorMessage={error?.message}
                  />
               )}
            />
         </Box>
         <Divider />
         <Box direction="column" gap="md">
            <Typography component="h4" variant="heading-4" color="strong">
               {translate('request-for-quotation.contact-information')}
            </Typography>
            {userToggles.hasVerifiedToggle && user && customer ? (
               <RfqContactInfo
                  email={user.mail}
                  firstName={user.firstname}
                  lastName={user.lastname}
                  company={customer.name}
                  country={addressDetailsFormValue?.deliveryAddress.countryName || '-'}
               />
            ) : (
               contactDetailsFormValue && (
                  <RfqContactInfo
                     email={contactDetailsFormValue.email}
                     firstName={contactDetailsFormValue.firstName}
                     lastName={contactDetailsFormValue.lastName}
                     company={contactDetailsFormValue.companyName}
                     country={contactDetailsFormValue.country.value || '-'}
                     phone={
                        contactDetailsFormValue.phone.phone
                           ? `+${contactDetailsFormValue.phone.countryInfo?.prefix} ${contactDetailsFormValue.phone.phone}`
                           : '-'
                     }
                  />
               )
            )}
         </Box>
         <Divider />

         <Box direction="column" gap="sm">
            <Typography component="h4" variant="heading-4" color="strong">
               {translate('request-for-quotation.delivery-information')}
            </Typography>
            {addressDetailsFormValue && (
               <RfqDeliveryInfo
                  address={addressDetailsFormValue.deliveryAddress}
                  timeframe={addressDetailsFormValue.timeframe.value}
                  leadTimeEnabled={leadTimeEnabled}
                  isMtsLocalAddress={deliveryIsMtsLocal}
               />
            )}
            {deliveryAreaFormValue && (
               <RfqDeliveryInfo
                  country={deliveryAreaFormValue.country.value}
                  city={deliveryAreaFormValue.city}
                  postalCode={deliveryAreaFormValue.postalCode}
                  timeframe={deliveryAreaFormValue.timeFrame.value}
                  leadTimeEnabled={leadTimeEnabled}
                  isMtsLocalAddress={deliveryIsMtsLocal}
               />
            )}
         </Box>
      </Box>
   );
};
